import { produce } from 'immer';
import { type CancelState } from './CancelComponent';
import { FTC_SKIP_TO_CANCEL_REASON } from './utils/defs';

export enum Navigation {
  Pause = '/cancel/pause/',
  // none of the routes here are actually used other than /pause
  PrimaryReason = '/cancel/primary-reason/',
  Winback = '/cancel/winback/',
  SecondaryReason = '/cancel/secondary-reason/',
  Confirm = '/cancel/confirm/',
  Success = '/cancel/success/',
}

export const navigationOrder: Navigation[] = [
  Navigation.Pause,
  Navigation.PrimaryReason,
  Navigation.Winback,
  Navigation.SecondaryReason,
  Navigation.Confirm,
];

export function getNext(
  value:
    | string
    | {
        cancelReasonId: number[];
        cancelReasonText: string | null;
        cancelFreeTextHeader: string | null;
      }
    | undefined,
  pathname: string,
  showWinback: boolean | undefined,
  cancelState: CancelState,
  skipToEnd?: boolean
) {
  if (pathname === Navigation.Pause) {
    // If user clicks "Skip" cta from the pause screen, set cancel reason to hardcoded id of FTC_SKIP_TO_CANCEL_REASON.
    return skipToEnd
      ? produce(cancelState, (draft) => {
          draft.next = Navigation.Confirm;
          draft.cancelReasonId = FTC_SKIP_TO_CANCEL_REASON;
        })
      : produce(cancelState, (draft) => {
          draft.next = Navigation.PrimaryReason;
        });
  }
  if (pathname === Navigation.PrimaryReason) {
    // If user clicks "Skip" cta from the primary reason screen and has selected a primary reason, record that reason. Otherwise, use the hardcoded id of FTC_SKIP_TO_CANCEL_REASON.
    const primaryReasonValue =
      value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : {
            cancelReasonId: null,
            cancelReasonText: null,
            cancelFreeTextHeader: null,
          };
    const { cancelReasonId, cancelReasonText, cancelFreeTextHeader } =
      primaryReasonValue;
    if (skipToEnd) {
      return produce(cancelState, (draft) => {
        draft.cancelReasonId = cancelReasonId?.length
          ? cancelReasonId[0]
          : FTC_SKIP_TO_CANCEL_REASON;
        draft.cancelReasonText = cancelReasonText;
        draft.cancelFreeTextHeader = cancelFreeTextHeader;
        draft.next = Navigation.Confirm;
      });
    } else if (showWinback) {
      return produce(cancelState, (draft) => {
        draft.cancelReasonId = cancelReasonId?.length
          ? cancelReasonId[0]
          : null;
        draft.cancelReasonText = cancelReasonText;
        draft.cancelFreeTextHeader = cancelFreeTextHeader;
        draft.next = Navigation.Winback;
      });
    } else {
      return produce(cancelState, (draft) => {
        draft.cancelReasonId = cancelReasonId?.length
          ? cancelReasonId[0]
          : null;
        draft.cancelReasonText = cancelReasonText;
        draft.cancelFreeTextHeader = cancelFreeTextHeader;
        draft.next = skipToEnd
          ? Navigation.Confirm
          : Navigation.SecondaryReason;
      });
    }
  }
  if (pathname === Navigation.Winback) {
    // If user clicks "Skip" cta from the winback screen, set "next" to the confirmation screen. Selected cancel reason and dynamic copy for rendering on the confirmation page will populate from primary reason screen.
    return produce(cancelState, (draft) => {
      draft.next = skipToEnd ? Navigation.Confirm : Navigation.SecondaryReason;
    });
  }
  if (pathname === Navigation.SecondaryReason) {
    return produce(cancelState, (draft) => {
      const secondaryReasonValue =
        value && typeof value === 'object' && !Array.isArray(value)
          ? value
          : {
              cancelReasonId: null,
              cancelReasonText: null,
              cancelFreeTextHeader: null,
            };
      const { cancelReasonId, cancelFreeTextHeader } = secondaryReasonValue;
      draft.cancelSubreasonId = cancelReasonId?.length ? cancelReasonId : [];
      draft.next = Navigation.Confirm;
      if (cancelFreeTextHeader !== null) {
        draft.cancelFreeTextHeader = cancelFreeTextHeader;
      }
    });
  }
  if (pathname === Navigation.Confirm) {
    return produce(cancelState, (draft) => {
      const cancelFreeText = value && typeof value === 'string' ? value : null;
      draft.next = Navigation.Success;
      draft.cancelFreeText = cancelFreeText;
    });
  }
  return produce(cancelState, (draft) => {
    draft.next = null;
  });
}
